import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { ArrowRight, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { request } from '@/lib/utils';

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const formTopRef = useRef<HTMLDivElement>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 5) {
      newErrors.message = 'Message must be at least 5 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Scroll once whatever we just showed - field errors or the sending
  // panel - has actually been painted, not before: scrolling first and
  // rendering the content after is what let the target land out of view
  // (the new content's height, and the keyboard closing on mobile, both
  // shift layout right after the scroll would have started).
  useEffect(() => {
    if (scrollTrigger === 0) return;

    let frame2 = 0;
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
    };
  }, [scrollTrigger]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = validateForm();

    // Dismiss the mobile keyboard and queue a scroll to the top of the
    // form - whether we're about to reveal validation errors or head into
    // the sending state, the user needs to see it either way.
    (document.activeElement as HTMLElement | null)?.blur();
    setScrollTrigger(t => t + 1);

    if (!isValid) {
      return;
    }

    setStatus('sending');
    setErrorMessage('');

    try {
      await request('/marketing/contact', 'POST', {...formData});

      setStatus('success');
      setFormData({ name: '', email: '', message: '' });

      // Reset success message after 5 seconds
      setTimeout(() => {
        setStatus('idle');
      }, 5000);

    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.'
      );
    }
  };

  return (
    <StyledContactForm ref={formTopRef}>
      <h2 className="serif">Send a Message</h2>
      <p>We typically respond within 1-2 business days.</p>

      {status === 'sending' && (
        <StatusPanel key="sending" className="sending">
          <Loader2 size={20} />
          <span>Sending your message…</span>
        </StatusPanel>
      )}

      {status === 'success' && (
        <StatusPanel key="success" className="success">
          <CheckCircle2 size={20} />
          <span>Thank you! Your message has been sent successfully. We'll get back to you soon.</span>
        </StatusPanel>
      )}

      {status === 'error' && (
        <StatusPanel key="error" className="error">
          <AlertCircle size={20} />
          <span>{errorMessage || 'Something went wrong. Please try again.'}</span>
        </StatusPanel>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <FormField>
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              disabled={status === 'sending'}
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <FieldError>{errors.name}</FieldError>}
          </FormField>

          <FormField>
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={handleChange}
              disabled={status === 'sending'}
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <FieldError>{errors.email}</FieldError>}
          </FormField>
        </div>

        <FormField>
          <textarea
            name="message"
            placeholder="Your Message"
            value={formData.message}
            onChange={handleChange}
            disabled={status === 'sending'}
            className={errors.message ? 'error' : ''}
          />
          {errors.message && <FieldError>{errors.message}</FieldError>}
        </FormField>

        <button
          type="submit"
          className={`primary${status === 'sending' ? ' sending' : ''}`}
          disabled={status === 'sending'}
        >
          {status === 'sending' ? 'Sending...' : 'Send Message'}
          {status === 'sending' ? (
            <Loader2 size={16} className="spin" />
          ) : (
            <ArrowRight size={16} />
          )}
        </button>
      </form>
    </StyledContactForm>
  );
};

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const popIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.4);
  }
  70% {
    opacity: 1;
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
`;

const StyledContactForm = styled.div`
  flex: 1.5;
  /* Bump this if the page has a fixed/sticky header, so scrolling to the
     top of the form doesn't leave it tucked behind it. */
  scroll-margin-top: 24px;

  h2 {
    font-size: 42px;
    margin-bottom: 16px;
  }

  p {
    font-size: 16px;
    color: var(--grey);
    margin-bottom: 40px;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 24px;

    .form-row {
      display: flex;
      gap: 24px;

      @media (max-width: 768px) {
        flex-direction: column;
      }
    }

    input, textarea {
      width: 100%;
      box-sizing: border-box;
      padding: 16px;
      font-size: 14px;
      border-radius: 6px;
      border: 1px solid rgba(var(--greyr), 0.3);
      background: #fafafa;
      transition: border-color 0.2s;

      &:focus {
        outline: none;
        border-color: var(--black);
      }

      &.error {
        border-color: #e74c3c;
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    textarea {
      min-height: 150px;
      resize: vertical;
    }

    button {
      align-self: flex-start;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 20px;
      background-color: var(--black);
      color: var(--white);
      border: 1px solid var(--black);
      border-radius: 8px;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s;
      svg{
          transition-duration: 0.3s;
      }

      &:hover:not(:disabled) {
        background-color: #333;
        svg{
            transform: translateX(2px);
          }

      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      &.sending svg {
        animation: ${spin} 0.8s linear infinite;
      }

      @media (prefers-reduced-motion: reduce) {
        &.sending svg {
          animation: none;
        }
      }
    }
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 8px;
`;

const FieldError = styled.span`
  font-size: 12px;
  color: #e74c3c;
  padding-left: 4px;
`;

const StatusPanel = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 24px;
  font-size: 14px;
  line-height: 1.5;
  animation: ${fadeSlideIn} 0.35s ease-out;

  svg {
    flex-shrink: 0;
  }

  &.sending {
    background: #fafafa;
    border: 1px solid rgba(var(--greyr), 0.3);
    color: var(--black);

    svg {
      animation: ${spin} 0.8s linear infinite;
    }
  }

  &.success {
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    color: #155724;

    svg {
      animation: ${popIn} 0.4s ease-out;
    }
  }

  &.error {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;

    svg {
      animation: ${popIn} 0.4s ease-out;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    svg {
      animation: none;
    }
  }
`;

export default ContactForm;
